import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toDiscountDisplay } from '@/services/settlement/settlementPromotion/discountValue'
import styled from 'styled-components'
import { HStack, Modal, VStack } from '@musinsa/mwms-ds'
import { FieldItem } from '@/components/content/FieldItem'
import SelectBox from '@/components/content/SelectBox_new'
import Input from '@/components/content/Input'
import { DatePickerInput } from '@/components/content/DatePicker/DatePickerInput'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Text from '@/components/content/Text'
import { colorSystem } from '@/styles/theme'
import { SearchableMultiSelect } from '@/components/content/SearchableMultiSelect'
import SearchableSelectBox from '@/components/content/SearchableSelectBox'
import { Radio } from '@/components/content/Radio'
import RadioGroup from '@/components/content/RadioGroup'
import Button from '@/components/content/Button'
import {
  getInitForm,
  kindFieldsOf,
  limitDiscountInput,
  MAX_TIERS,
  MAX_SKU_IDS,
  MIN_TIERS,
  parseSkuIds,
  PERIOD_START_MIN_DATE,
  PRODUCT_KIND,
  SKU_ID_LENGTH,
  settleMonthStart,
  settlementPromotionSchema,
  SettlementPromotionSchemaType,
} from '../schemas/postPromotionSchemas'
import {
  useGetPromotionChargeBases,
  useGetServiceTypeList,
  usePostBrandList,
  usePostMFSPartnerList,
  usePostPromotion,
  usePutPromotion,
} from '@/services/settlement/settlementPromotion/useSettlementPromotionQueries'
import dayjs from 'dayjs'
import minMax from 'dayjs/plugin/minMax'
import MToast from '@/utils/modal/MToast'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import {
  PARCEL_SERVICE_TYPES,
  PROMOTION_REMARK_MAX_LENGTH,
  PROMOTION_SKU_TARGET_FLAG,
  PROMOTION_TITLE_MAX_LENGTH,
  PromotionOrderType,
  PromotionTier,
  ServiceTypeCode,
} from '@/services/settlement/settlementPromotion/types'
import { useGetLogisticsCategories } from '@/services/commonCode/useCommonCodeQueries'
import { createCategoryLabeler } from '@/services/settlement/settlementPromotion/logisticsCategoryLabel'
import {
  buildPromotionAxisOptions,
  INBOUND_SETTLEMENT_UNIT_TYPE_FLAG,
  parseChargeItemValue,
  toChargeItemValue,
} from '@/services/settlement/settlementPromotion/serviceChargeItem'
import { useFeatureFlag, useFeatureFlagBoolean } from '@/services/feature/useFeatureQueries'
import { AxiosError } from 'axios'
import { EXCLUDED_SERVICE_TYPES } from './PromotionManagementForm'

interface Props {
  open: boolean
  defaultValues: (SettlementPromotionSchemaType & { promotionId?: number }) | null
  refetch: () => void
  onClose: (closeCb?: () => void) => void
}

// 타입 가드 함수 추가
const isEditMode = (defaultValues: Props['defaultValues']): defaultValues is SettlementPromotionSchemaType & { promotionId: number } => {
  return defaultValues !== null && defaultValues.promotionId !== undefined
}

// LOGISPRD-10268 — 국가 축 (프로모션 고도화, LLD-LP10200 ADR D1: 단가 도메인 국가 체계 준용)
dayjs.extend(minMax)

// 정률 할인율은 0 초과 100 이하. 0% 는 할인이 아니라 등록해도 정산 금액이 그대로라서 받지 않는다.
// 대상을 빼려면 정액 0 원을 쓴다 (ADR-20260720-01 §1, LOGISPRD-11314)
const RATE_RANGE_MESSAGE = '할인율은 0보다 크고 100 이하여야 합니다.'
// 구간(tier)은 0% 를 받는다 — 누적 수량이 적은 첫 구간을 0% 로 두는 것이 대표 사용 패턴이다
// (ADR-20260720-01 Tier 정의, VDC 출고택배비)
const TIER_RATE_RANGE_MESSAGE = '할인율은 0 이상 100 이하여야 합니다.'
const outOfRateRange = (raw: string, allowZero = false) => {
  if (!raw) return false
  const value = Number(raw)
  return Number.isNaN(value) || value < 0 || (!allowZero && value === 0) || value > 100
}

const EMPTY_TIER = { fromQuantity: '', toQuantity: '', discountValue: '' }
// 기간 프로모션을 고르면 구간을 이만큼 비어 있는 채로 깔아 둔다
const DEFAULT_TIER_ROWS = 2

// tiers 폼값(string) → API 계약 (toQuantity '' = null 무한).
// 할인값만 문자열로 보낸다 — 정액 16자리가 숫자로 바뀌면 반올림된다 (LOGISPRD-11361)
const toTierPayload = (tiers: SettlementPromotionSchemaType['tiers']): PromotionTier[] =>
  tiers.map((tier) => ({
    fromValue: Number(tier.fromQuantity),
    toValue: tier.toQuantity === '' ? null : Number(tier.toQuantity),
    discountValue: tier.discountValue,
  }))

function PromotionFormModal({ open, onClose, defaultValues, refetch }: Props) {
  const form = useForm<SettlementPromotionSchemaType>({
    resolver: zodResolver(settlementPromotionSchema),
    defaultValues: getInitForm(defaultValues ?? {}),
    mode: 'onChange',
  })

  const values = form.watch()

  const isEdit = isEditMode(defaultValues)

  // 초기화 effect 가 최신 prop 을 쓰되, 매 렌더 새로 만들어지는 객체를 deps 로 받지 않게 한다
  const defaultValuesRef = useRef(defaultValues)
  defaultValuesRef.current = defaultValues

  // 일별로 되돌릴 때 치워 둔 구간. 다시 기간으로 오면 입력하던 값을 되돌려 준다
  const stashedTiersRef = useRef<SettlementPromotionSchemaType['tiers']>([])

  // 국가·주문유형을 한 번이라도 건드렸는가 (LOGISPRD-11448). `dirtyFields` 로 잡으면 축을 원래 값으로
  // 되돌린 순간 키가 지워져, 그 사이 고른 뒤 축의 값이 조합에 없어도 그대로 남는다
  const [isAxisEdited, setIsAxisEdited] = useState(false)
  // 서비스 항목을 택배비로 옮겼는지 판정할 직전 값 — 저장분이 처음부터 택배비인 경우와 갈라야 한다
  const wasParcelRef = useRef(false)

  // 모달이 상시 마운트라 useForm 의 defaultValues 는 첫 생성 때만 반영된다.
  // 그대로 두면 저장 후 다시 열었을 때 예전 값이 그려져 수정이 안 된 것처럼 보인다 — 열리는 순간 한 번 다시 채운다.
  // deps 에 defaultValues 객체를 넣으면 부모가 매 렌더 새 객체를 만들어 입력 중에도 값이 되돌아간다
  useEffect(() => {
    if (!open) return
    const initForm = getInitForm(defaultValuesRef.current ?? {})
    form.reset(initForm)
    setIsAxisEdited(false)
    wasParcelRef.current = PARCEL_SERVICE_TYPES.includes(initForm.serviceType as ServiceTypeCode)
    // 모달은 언마운트되지 않아 ref 가 살아 있다. 비우지 않으면 앞서 치워 둔 구간이 다음에 연 등록 폼에 되살아난다
    stashedTiersRef.current = []
    if (isEdit) {
      form.trigger()
    }
  }, [open, defaultValues?.promotionId])

  const { data: partnerIds } = usePostMFSPartnerList()
  // 브랜드는 업체에 종속된다 — 업체를 골라야 브랜드 셀렉트가 열리고, 등록 스키마도 업체 없는 브랜드 지정을 거부한다
  const { data: partnerBrands } = usePostBrandList({ partnerIds: values.partnerIds ? [values.partnerIds] : undefined })
  const brandOptions = partnerBrands ?? []
  const { data: serviceTypeList } = useGetServiceTypeList()
  // LOGISPRD-11315 — 등록 가능한 (국가·주문유형·서비스 항목·정산 단위) 조합. anchor 실물이라 없는 조합은 내려오지 않는다
  const { data: chargeBaseOptions = [] } = useGetPromotionChargeBases()
  // LOGISPRD-10268 — 물류카테고리 옵션 (moms 공통코드, 단가관리와 동일 원천)
  const { data: logisticsCategories } = useGetLogisticsCategories()
  const categoryLabelOf = useMemo(() => createCategoryLabeler(logisticsCategories), [logisticsCategories])
  // LP8658: 박스단위 입고 정산 feature flag. OFF 면 INBOUND 분화 없이 레거시 단일 항목 (fail-safe 기본 false)
  const { data: isInboundSettlementUnitTypeEnabled } = useFeatureFlag(INBOUND_SETTLEMENT_UNIT_TYPE_FLAG)
  // LOGISPRD-13627 — 상품 종류(SKU ID) 노출. 등록에서만 연다. 저장된 상품 프로모션은 서버가 editable=false 로 내려 수정 모달이 열리지 않는다
  const { data: isSkuTargetEnabled } = useFeatureFlagBoolean(PROMOTION_SKU_TARGET_FLAG.key, PROMOTION_SKU_TARGET_FLAG.objectKey)
  const canPickProduct = !!isSkuTargetEnabled && !isEdit
  // 일별 선정 순위는 상품 → 일반 → 전체다 (mass-settlement PromotionSelector). FF 가 꺼져 있으면 상품이 없어 일반부터 센다
  const rankOffset = isSkuTargetEnabled ? 1 : 0
  const rankOrder = isSkuTargetEnabled ? '① 상품 → ② 일반 → ③ 전체' : '① 일반 → ② 전체'

  const {
    formState: { isValid, errors },
  } = form

  // 시안 v4 — 프로모션 종류가 적용범위 입력을 가른다. 종류에 없는 축은 비활성 + 값 비움
  const kind = values.promotionKind
  const kindFields = kindFieldsOf(kind)
  // onChange 폼이라 키입력마다 리렌더된다. SKU 입력 건수는 그 칸이 바뀔 때만 다시 센다
  const skuIdCount = useMemo(() => parseSkuIds(values.skuIdsText).length, [values.skuIdsText])

  // 종류를 바꾸면 그 종류가 받지 않는 축의 값을 비운다 (시안 applyScopeFields 와 동일)
  useEffect(() => {
    if (!kind) return
    if (!kindFields.partner && values.partnerIds) form.setValue('partnerIds', '', { shouldValidate: true })
    if (!kindFields.brand && values.brandIds.length) form.setValue('brandIds', [], { shouldValidate: true })
    if (!kindFields.category && values.categoryCodes.length) form.setValue('categoryCodes', [], { shouldValidate: true })
    if (kind !== 'PRODUCT' && values.skuIdsText) form.setValue('skuIdsText', '', { shouldValidate: true })
  }, [kind])

  const tierFieldArray = useFieldArray({ control: form.control, name: 'tiers' })

  // 할인 방식을 바꾸면 입력 규칙이 통째로 달라진다 — 정률은 소수 2자리·100 이하, 정액은 정수다.
  // 값을 남기면 정액 칸에 소수점이, 정률 칸에 100 초과가 그대로 남는다. 기간 프로모션은 구간 할인값도 같이 비운다.
  // 일별로 치워 둔 구간까지 비워야 한다 — 안 그러면 기간으로 되돌아올 때 옛 방식 값이 되살아난다 (LOGISPRD-11443)
  const clearDiscountValues = () => {
    const withoutDiscount = (tiers: SettlementPromotionSchemaType['tiers']) => tiers.map((tier) => ({ ...tier, discountValue: '' }))

    form.setValue('discountValue', '', { shouldValidate: true })
    stashedTiersRef.current = withoutDiscount(stashedTiersRef.current)
    if (tierFieldArray.fields.length) {
      // 행마다 setValue 하면 그 수만큼 검증이 돈다. 한 번에 갈아 끼우고 검증은 아래 tiers 재검증 효과에 맡긴다
      tierFieldArray.replace(withoutDiscount(form.getValues('tiers')))
    }
  }

  // 기간으로 바꾸면 빈 구간 2행을 깔아 준다 (수정 모드로 들어와 이미 구간이 있으면 그대로 둔다).
  // 일별로 되돌리면 행을 치운다 — 화면에서 사라진 구간의 오류가 남아 등록 버튼이 계속 잠기는 것을 막는다
  useEffect(() => {
    if (values.settlementCycle === 'MONTHLY' && tierFieldArray.fields.length === 0) {
      const restored = stashedTiersRef.current
      tierFieldArray.replace(restored.length ? restored : Array.from({ length: DEFAULT_TIER_ROWS }, () => ({ ...EMPTY_TIER })))
      stashedTiersRef.current = []
    }
    if (values.settlementCycle === 'DAILY' && tierFieldArray.fields.length > 0) {
      stashedTiersRef.current = form.getValues('tiers')
      tierFieldArray.replace([])
    }
  }, [values.settlementCycle])

  // 손대지 않은 구간 행의 오류도 표 아래에 보여야 한다 — react-hook-form 의 onChange 검증은
  // 방금 바뀐 필드 경로의 오류만 formState 에 쓴다. 등록 버튼이 잠긴 이유가 화면에 없으면 안 된다.
  // 구간 오류는 스키마 최상위 superRefine 에도 있어(할인값·종료 수량) 다른 필수값이 다 채워진 뒤에야 만들어지므로,
  // 구간 값만이 아니라 폼 전체가 바뀔 때마다 tiers 를 다시 검증해 오류 트리를 통째로 채운다 (LOGISPRD-11429)
  const formSnapshot = JSON.stringify(values)
  useEffect(() => {
    if (values.settlementCycle !== 'MONTHLY') return
    form.trigger('tiers')
  }, [formSnapshot])

  // 구간 행 단위 오류 — errors.tiers 는 행별 객체 배열이라 배열 자체의 message 만 보면 화면에 아무것도 안 뜬다.
  // 첫 오류 하나를 표 아래에 보여 준다 (시작 > 종료, 구간 불연속 등)
  const firstTierRowError = (Array.isArray(errors.tiers) ? errors.tiers : [])
    .flatMap((rowError) => (rowError ? Object.values(rowError) : []))
    .map((fieldError) => (fieldError as { message?: string } | undefined)?.message)
    .find(Boolean)

  // 구간은 이전 구간 종료 수량 + 1 에서 시작한다. 이전 종료를 입력·수정하면 다음 시작을 따라 갱신한다.
  // deps 는 종료 수량을 이어붙인 문자열이다 — react-hook-form 은 값 배열을 제자리에서 고쳐
  // `values.tiers` 참조가 그대로라 배열을 deps 에 두면 효과가 다시 돌지 않는다
  const tierToQuantities = values.tiers.map((tier) => tier.toQuantity).join(',')
  useEffect(() => {
    values.tiers.forEach((tier, index) => {
      if (index === 0) return
      const prevTo = values.tiers[index - 1].toQuantity
      const nextFrom = prevTo === '' ? '' : String(Number(prevTo) + 1)
      if (tier.fromQuantity !== nextFrom) {
        form.setValue(`tiers.${index}.fromQuantity`, nextFrom, { shouldValidate: true })
      }
    })
  }, [tierToQuantities, tierFieldArray.fields.length])

  const promotionMutation = usePostPromotion()
  const putPromotionMutation = usePutPromotion()

  // 시안 v4 scopeCap — 종류별 적용범위 안내
  const scopeCaption = !kind
    ? '— 프로모션 종류를 먼저 선택해 주세요.'
    : kind === 'ALL'
      ? '— 전체 프로모션은 적용범위를 입력하지 않습니다.'
      : kind === 'PRODUCT'
        ? '— SKU ID 만 입력합니다. 업체 · 브랜드는 SKU ID 로 식별합니다.'
        : PARCEL_SERVICE_TYPES.includes(values.serviceType as ServiceTypeCode)
        ? '— 택배비는 업체만 지정할 수 있습니다. (브랜드 · 카테고리 지정 불가)'
        : '— 업체 · 브랜드 · 카테고리 중 최소 1개 이상을 지정해 주세요. (브랜드는 업체 선택 후)'

  const isMonthly = values.settlementCycle === 'MONTHLY'
  // 기간 프로모션은 시작일·종료일 하한이 다르다 (일별은 둘 다 오늘)
  //  - 시작일: 카테고리 적재 시작일(2026-07-12) 까지 소급 가능
  //  - 종료일: 미마감 정산월 시작일. 마감일(26일, 12월은 31일)이 지난 정산월은 못 고른다
  const today = dayjs().format('YYYY-MM-DD')
  const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD')
  // 일 단위 수정만 시작일이 오늘보다 뒤여야 한다 (PromotionValidator.updateValidate — 라인 계산 중 즉시
  // 적용이라 지난 라인을 다시 쓸 수 없다). 기간 단위는 과거 라인을 읽기만 해 수정도 등록과 같은 하한을
  // 받는다 — 여기서 내일로 올리면 BE 가 받는 정상 수정이 화면에서 막힌다 (LOGISPRD-11444)
  const startMinDate = isMonthly ? PERIOD_START_MIN_DATE : isEdit ? tomorrow : today
  const endMinDate = isMonthly ? settleMonthStart() : today
  const isRate = values.discountType === 'RATE'
  // 택배비류는 카테고리 단위 등록 거부 (기획 확정 2026-07-22, LLD-LP10200 §6 — 업체·브랜드 단위까지만)
  const isParcelServiceType = PARCEL_SERVICE_TYPES.includes(values.serviceType as ServiceTypeCode)

  // 국가 → 주문유형 → 서비스 항목은 조합 조회 결과가 정한다 (LOGISPRD-11315 · buildPromotionAxisOptions)
  const { isCompositeServiceValue, countryOptions, orderTypeOptions, serviceItemOptions } = useMemo(
    () =>
      buildPromotionAxisOptions({
        chargeBases: chargeBaseOptions,
        serviceTypeList: serviceTypeList ?? [],
        excludedServiceTypes: EXCLUDED_SERVICE_TYPES,
        isUnitTypeEnabled: !!isInboundSettlementUnitTypeEnabled,
        country: values.country,
        orderType: values.orderType,
      }),
    [chargeBaseOptions, serviceTypeList, isInboundSettlementUnitTypeEnabled, values.country, values.orderType],
  )

  // 앞 축을 바꾸면 뒤 축의 선택이 조합에 없을 수 있다 — 비워서 다시 고르게 한다 (빈 값은 스키마가 required 로 잡는다).
  // 서비스 항목은 단위까지 봐야 한다 — INBOUND BOX 를 고른 뒤 PCS anchor 만 있는 조합으로 옮기면 셀렉트만 빈칸이 되고 값은 남는다
  const selectedServiceItemValue = values.serviceType
    ? isCompositeServiceValue
      ? toChargeItemValue(values.serviceType, values.unitType)
      : values.serviceType
    : ''
  // 저장분을 그대로 그린 상태에서는 지우지 않는다 (LOGISPRD-11448). 조합 조회가 오기 전 폴백 목록은
  // 구 매핑(한국→국내)뿐이라 한국·해외(GPC) 저장분이 그 사이 비워지고, 조합이 온 뒤에도 저장된 anchor 가
  // 목록에서 빠져 있으면(LP8658 flag off 구간의 BOX 등) 다시 고를 항목 없이 값만 사라진다
  useEffect(() => {
    if (isEdit && !isAxisEdited) return
    if (values.orderType && !orderTypeOptions.some((option) => option.value === values.orderType)) {
      form.setValue('orderType', undefined as unknown as PromotionOrderType, { shouldValidate: true })
    }
    if (selectedServiceItemValue && !serviceItemOptions.some((option) => option.value === selectedServiceItemValue)) {
      form.setValue('serviceType', '', { shouldValidate: true })
      form.setValue('unitType', 'PCS', { shouldValidate: true })
    }
  }, [orderTypeOptions, serviceItemOptions, selectedServiceItemValue, values.orderType, isEdit, isAxisEdited])

  // 서비스 항목을 택배비로 옮기면 브랜드·카테고리를 비운다 (LOGISPRD-11448).
  // 택배비는 업체까지만 지정할 수 있어 두 입력이 비활성으로 잠기는데, 값이 남아 있으면 스키마가 오류를 내고
  // 고칠 수 있는 필드가 화면에 없다 — 수정에서는 종류를 바꿔 비우는 탈출로도 막혀 있다.
  // 처음부터 택배비로 저장된 건은 건드리지 않는다 (열자마자 저장분이 지워지지 않게)
  useEffect(() => {
    const wasParcel = wasParcelRef.current
    wasParcelRef.current = isParcelServiceType
    if (!isParcelServiceType || wasParcel) return
    if (values.brandIds.length) form.setValue('brandIds', [], { shouldValidate: true })
    if (values.categoryCodes.length) form.setValue('categoryCodes', [], { shouldValidate: true })
  }, [isParcelServiceType])

  // 상품 종류의 택배비 거부는 종류 칸(promotionKind) 오류로 난다. onChange 검증은 방금 바뀐 서비스 항목의 오류만 쓰므로
  // 종류 칸을 다시 검증해야 등록 버튼이 잠긴 이유가 화면에 뜬다. 택배비에서 벗어나면 같은 검증이 오류를 지운다
  useEffect(() => {
    if (kind === 'PRODUCT') form.trigger('promotionKind')
  }, [isParcelServiceType])

  // 기간 겹침 거부(CONFLICT_PROMOTION·ALL_BRANDS_CONFLICT) 핸들링 제거 (기획 확정 2026-07-27):
  // 겹치는 프로모션은 거부 없이 각각 독립 적용되므로 BE 가 해당 오류를 더 이상 반환하지 않는다.
  const handleError = (error: AxiosError<ApiResponseV2<unknown>>) => {
    MAlert.show(Utils.getApiErrMessage(error, '프로모션 등록에 실패했습니다.'))
  }

  const onSubmit = async (formData: SettlementPromotionSchemaType) => {
    const effectiveBrandIds =
      brandOptions.length > 0 && formData.brandIds.length === brandOptions.length
        ? [] // 전체선택 = 전체 대상(ALL) 정규화 (기획 확정) — 등록 이후 그 업체에 추가되는 브랜드도 적용 대상이 된다
        : formData.brandIds
    const selectedBrands = effectiveBrandIds.map((brandId) => {
      const option = brandOptions.find((opt) => opt.brandId === brandId)
      return {
        brandId: brandId,
        brandName: option?.brandName || '', // 여기서 brandName을 찾아서 변환
      }
    })

    // LOGISPRD-10268 — 축 각각 전체/특정 자유 조합. 빈 값 = 전체(ALL), 적용 단위는 BE 가 판정.
    // 안전망: 수동으로 모든 항목을 체크한 경우도 전체(ALL)로 정규화 — 항목별 특정 행 수백 개 방지
    const allCategoriesSelected = (logisticsCategories?.length ?? 0) > 0 && formData.categoryCodes.length === logisticsCategories!.length
    // 프로모션 종류는 화면 전용 — 서버는 축 조합으로 적용 단위를 판정한다
    const { promotionKind: _promotionKind, skuIdsText: _skuIdsText, ...payloadBase } = formData
    const enhancementPayload = {
      country: formData.country,
      orderType: formData.orderType,
      settlementCycle: formData.settlementCycle,
      categoryCodes: allCategoriesSelected ? [] : formData.categoryCodes,
      // 일별 = 단일 할인값, 기간 = tier 구간 (단일값은 0 전송하지 않고 tier 로만)
      tiers: formData.settlementCycle === 'MONTHLY' ? toTierPayload(formData.tiers) : undefined,
      // 기간이면 구간 기준 축이 필수다 (BE INVALID_PROMOTION_TIER). 현재 값은 수량 하나뿐
      tierType: formData.settlementCycle === 'MONTHLY' ? ('QUANTITY' as const) : undefined,
      // 비고 — 빈 값·공백만 입력은 보내지 않는다 (미입력 건은 컬럼을 NULL 로 둔다)
      remark: formData.remark.trim() || undefined,
      // 상품 종류만 SKU 를 보낸다 (LOGISPRD-13627). 서버가 이 목록으로 적용 단위를 PRODUCT 로 판정한다
      skuIds: formData.promotionKind === 'PRODUCT' ? parseSkuIds(formData.skuIdsText) : undefined,
    }

    try {
      if (isEdit) {
        await putPromotionMutation.mutateAsync({
          ...payloadBase,
          ...enhancementPayload,
          discountValue: formData.settlementCycle === 'DAILY' ? formData.discountValue : '0',
          promotionId: defaultValues.promotionId,
          brands: selectedBrands,
          partnerIds: formData.partnerIds ? [formData.partnerIds] : [],
          applyToAllPartners: !formData.partnerIds,
          applyToAllBrands: effectiveBrandIds.length === 0,
        })
      } else {
        await promotionMutation.mutateAsync({
          ...payloadBase,
          ...enhancementPayload,
          discountValue: formData.settlementCycle === 'DAILY' ? formData.discountValue : '0',
          brands: selectedBrands,
          partnerIds: formData.partnerIds ? [formData.partnerIds] : [],
          applyToAllPartners: !formData.partnerIds,
          applyToAllBrands: effectiveBrandIds.length === 0,
        })
      }
      form.reset()
      await refetch()
      MToast.show(isEdit ? '프로모션 수정이 완료되었습니다' : '프로모션 등록이 완료되었습니다')
      onClose(() => form.reset())
    } catch (error) {
      handleError(error as AxiosError<ApiResponseV2<unknown>>)
    }
  }

  const discountValueLabel = isRate ? '할인율' : '할인금액'

  return (
    <form>
      <Modal
        open={open}
        size="md"
        customWidth={560}
        title={isEdit ? '프로모션 수정' : '프로모션 등록'}
        buttons={[
          { kind: 'secondary', content: '취소', onClick: () => onClose(() => form.reset()) },
          {
            type: 'submit',
            kind: 'primary_light',
            content: isEdit ? '수정' : '등록',
            disabled: !isValid || promotionMutation.isPending || putPromotionMutation.isPending,
            onClick: (e) => {
              e.preventDefault()
              form.handleSubmit(onSubmit)()
            },
          },
        ]}
        onClose={() => onClose(() => form.reset())}
      >
        {/* 시안 v4 (MFS_프로모션관리_화면.html) 마크업 구조를 그대로 옮긴다 — .frow 그리드(150px 1fr) + .hint 전체 폭 */}
        <Body>
          <Row>
            <RowLabel>
              국가 <Req>*</Req>
            </RowLabel>
            <Controller
              control={form.control}
              name="country"
              render={({ field }) => (
                <Ctl
                  as="select"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    setIsAxisEdited(true)
                    field.onChange(e)
                  }}
                >
                  <option value="">국가를 선택해 주세요.</option>
                  {countryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Ctl>
              )}
            />
          </Row>
          <FieldError message={errors.country?.message} />

          {/* LOGISPRD-11315 — 국가와 짝으로 부과 기준을 고른다. 한국은 국내·해외(GPC) 둘 다, 일본은 해외만 열린다 */}
          <Row>
            <RowLabel>
              주문유형 <Req>*</Req>
            </RowLabel>
            <Controller
              control={form.control}
              name="orderType"
              render={({ field }) => (
                <Ctl
                  as="select"
                  {...field}
                  value={field.value ?? ''}
                  disabled={!values.country}
                  onChange={(e) => {
                    setIsAxisEdited(true)
                    field.onChange(e)
                  }}
                >
                  <option value="">{values.country ? '주문유형을 선택해 주세요.' : '국가를 먼저 선택해 주세요.'}</option>
                  {orderTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Ctl>
              )}
            />
          </Row>
          <FieldError message={errors.orderType?.message} />

          <Row>
            <RowLabel>
              서비스 항목 <Req>*</Req>
            </RowLabel>
            <Controller
              control={form.control}
              name="serviceType"
              render={({ field }) => (
                <Ctl
                  as="select"
                  disabled={!values.orderType}
                  value={
                    isCompositeServiceValue && values.serviceType
                      ? toChargeItemValue(values.serviceType, values.unitType)
                      : field.value ?? ''
                  }
                  onChange={(e) => {
                    const value = e.target.value
                    if (!isCompositeServiceValue) {
                      field.onChange(value)
                    } else {
                      const { serviceType, unitType } = parseChargeItemValue(value)
                      field.onChange(serviceType)
                      form.setValue('unitType', unitType, { shouldValidate: true })
                    }
                  }}
                >
                  <option value="">{values.orderType ? '서비스 항목을 선택해 주세요.' : '주문유형을 먼저 선택해 주세요.'}</option>
                  {serviceItemOptions.map((opt) => (
                    <option key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </Ctl>
              )}
            />
          </Row>
          <FieldError message={errors.serviceType?.message} />

          <Row>
            <RowLabel>
              프로모션명 <Req>*</Req>
            </RowLabel>
            <Controller
              control={form.control}
              name="title"
              render={({ field }) => <Ctl {...field} maxLength={PROMOTION_TITLE_MAX_LENGTH} placeholder="프로모션명을 입력해 주세요." />}
            />
          </Row>
          <FieldError message={errors.title?.message} />

          {/* 프로모션 종류 : 일반 / 전체 2종 (정책서 v3 §3.1 — 상품은 LOGISPRD-11338 로 뺐다) */}
          {/* 수정에서도 바꿀 수 있다 (LOGISPRD-11448). 종류를 바꾸면 그 종류가 받지 않는 축의 값이 비워지는데,
              등록 화면과 같은 동작이고 비워진 입력이 화면에 그대로 보인다 */}
          <Row>
            <RowLabel>
              프로모션 종류 <Req>*</Req>
            </RowLabel>
            <Controller
              control={form.control}
              name="promotionKind"
              render={({ field }) => (
                <Ctl as="select" {...field} value={field.value ?? ''}>
                  <option value="">프로모션 종류를 선택해 주세요.</option>
                  <option value="NORMAL">일반</option>
                  <option value="ALL">전체</option>
                  {canPickProduct && <option value="PRODUCT">{PRODUCT_KIND.label}</option>}
                </Ctl>
              )}
            />
          </Row>
          <Hint>
            {kind === 'NORMAL' && (
              <>
                <b>일반({1 + rankOffset}순위)</b> — 업체 · 브랜드 · 카테고리 조합으로 적용됩니다. 지정하지 않은 항목은 <b>전체</b>로 저장되며,{' '}
                <b>3개를 모두 전체로 둘 수는 없습니다.</b>
                <br />
                <b>브랜드는 업체아이디를 선택해야 지정할 수 있습니다.</b> (브랜드는 업체에 종속)
                {isParcelServiceType && (
                  <>
                    <br />
                    택배비는 송장 단위로 산정되어 <b>업체만 지정</b>할 수 있습니다. 따라서 <b>업체 지정은 필수</b>이며, 전체 업체에 적용하려면
                    종류를 <b>전체</b>로 등록해 주세요.
                  </>
                )}
              </>
            )}
            {kind === 'ALL' && (
              <>
                <b>전체({2 + rankOffset}순위)</b> — 모든 업체 · 모든 브랜드 · 모든 카테고리에 적용됩니다.
              </>
            )}
            {kind === 'PRODUCT' && (
              <>
                <b>상품(1순위)</b> — 입력한 SKU ID 에만 적용됩니다. 업체는 입력하지 않으며, 택배비에는 등록할 수 없습니다.
              </>
            )}
            {!kind && '프로모션 종류를 선택하기 전에는 적용범위를 입력할 수 없습니다.'}
          </Hint>
          <FieldError message={errors.promotionKind?.message} />

          {/* 적용범위 : 프로모션 종류에 따라 항목별로 활성/비활성 (정책서 v3 §3.2) */}
          <ScopeBox>
            <ScopeCaption>
              적용범위 <span>{scopeCaption}</span>
            </ScopeCaption>

            <Row className={kindFields.partner ? undefined : 'off'}>
              {/* 업체는 택배비일 때만 필수다. 상품은 업체를 입력하지 않는다 (LOGISPRD-14466). 그 밖에는 전체로 두고 카테고리를 지정하는 조합도 일반 프로모션이다 (LOGISPRD-11341) */}
              <RowLabel>업체아이디 {kind === 'NORMAL' && isParcelServiceType ? <Req>*</Req> : null}</RowLabel>
              <Controller
                control={form.control}
                name="partnerIds"
                render={({ field }) => (
                  <SearchableSelectBox
                    block
                    value={field.value}
                    placeholder={kindFields.partner ? '파트너를 선택해 주세요.' : '종류를 먼저 선택해 주세요.'}
                    hasAll
                    disabled={!kindFields.partner}
                    onChange={(value) => {
                      field.onChange(value ?? '')
                      form.setValue('brandIds', [], { shouldValidate: true })
                    }}
                    options={(partnerIds ?? []).map((id) => ({ value: id, label: id }))}
                  />
                )}
              />
            </Row>
            <FieldError message={errors.partnerIds?.message} />

            <Row className={kindFields.brand ? undefined : 'off'}>
              <RowLabel>브랜드명(브랜드ID)</RowLabel>
              <Controller
                name="brandIds"
                control={form.control}
                render={({ field }) => (
                  <SearchableMultiSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={
                      !kindFields.brand
                        ? '종류가 일반일 때만 지정할 수 있습니다.'
                        : !values.partnerIds
                          ? '업체아이디를 먼저 선택해 주세요.'
                          : '브랜드 (미선택 = 전체)'
                    }
                    options={Utils.createSelectOptions(brandOptions, 'brandName', 'brandId')}
                    disabled={!kindFields.brand || !values.partnerIds || isParcelServiceType}
                    // 업체가 전체면 브랜드도 전체가 되어 종류가 전체 프로모션과 같아진다 — 전체 행을 감춘다 (LOGISPRD-11341)
                    showSelectAll={Boolean(values.partnerIds)}
                    emptyMeansAll
                    triggerHeight={32}
                  />
                )}
              />
            </Row>
            {kindFields.brand && (
              <Hint>
                {values.partnerIds
                  ? `${values.partnerIds} 업체의 브랜드만 선택할 수 있습니다. 여러 건 선택 가능합니다.`
                  : '브랜드는 업체에 종속됩니다. 업체아이디를 먼저 선택해야 브랜드를 지정할 수 있습니다.'}
              </Hint>
            )}
            <FieldError message={errors.brandIds?.message} />

            <Row className={kindFields.category ? undefined : 'off'}>
              {/* 업체가 전체면 카테고리가 유일하게 남은 범위 축이라 필수다 (LOGISPRD-11341) */}
              <RowLabel>카테고리(물류) {kind === 'NORMAL' && !isParcelServiceType && !values.partnerIds ? <Req>*</Req> : null}</RowLabel>
              <Controller
                name="categoryCodes"
                control={form.control}
                render={({ field }) => (
                  <SearchableMultiSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!kindFields.category || isParcelServiceType}
                    placeholder={
                      !kindFields.category
                        ? '종류가 일반일 때만 지정할 수 있습니다.'
                        : values.partnerIds
                          ? '카테고리 (미선택 = 전체)'
                          : '카테고리를 선택해 주세요.'
                    }
                    // 업체가 전체일 때 카테고리까지 전체면 전체 프로모션과 같아져 등록이 막힌다 — 전체 행을 감춘다 (LOGISPRD-11341)
                    showSelectAll={Boolean(values.partnerIds)}
                    // 시안 v4 — 라벨 뒤에 코드를 괄호로 붙인다. 이름·코드 어느 쪽으로 검색해도 걸린다
                    options={(logisticsCategories ?? []).map((category) => ({
                      value: category.code,
                      label: categoryLabelOf(category.code),
                    }))}
                    emptyMeansAll
                    triggerHeight={32}
                  />
                )}
              />
            </Row>
            {kindFields.category && (
              <Hint>
                물류 카테고리 <b>소분류</b> 단위(물류카테고리코드)로 지정합니다. 전체 {logisticsCategories?.length ?? 0}건 — 드롭다운 상단 검색창에{' '}
                <b>카테고리명</b> 또는 <b>물류카테고리코드</b>를 입력해 좁혀 주세요.
                {!values.partnerIds && !isParcelServiceType && (
                  <>
                    <br />
                    업체아이디가 <b>전체</b>이므로 카테고리를 <b>1개 이상</b> 지정해야 합니다. 모두 전체로 두려면 종류를 <b>전체</b>로 등록해 주세요.
                  </>
                )}
              </Hint>
            )}
            <FieldError message={errors.categoryCodes?.message} />

            {kind === 'PRODUCT' && (
              <>
                <Row className="top">
                  <RowLabel>
                    SKU ID <Req>*</Req>
                  </RowLabel>
                  <Controller
                    control={form.control}
                    name="skuIdsText"
                    render={({ field }) => <SkuTextarea {...field} rows={4} placeholder="SKU ID 를 입력해 주세요. (쉼표 · 줄바꿈으로 구분)" />}
                  />
                </Row>
                <Hint>
                  SKU ID 는 {SKU_ID_LENGTH}자이며 최대 {MAX_SKU_IDS}개까지 입력할 수 있습니다. 입력 {skuIdCount}건
                </Hint>
                <FieldError message={errors.skuIdsText?.message} />
              </>
            )}
          </ScopeBox>

          <Hint>
            <b>중복 등록</b> — 국가 · 서비스 항목 · 프로모션 종류 · 적용범위 · 정산 방식 · 할인 방식 · 할인값이 모두 동일한 건만 기간이 겹칠 때
            등록할 수 없습니다. 항목이 하나라도 다르면 기간이 겹쳐도 등록할 수 있습니다.
            <br />
            <b>일별 프로모션</b>은 대상이 겹치면 <b>{rankOrder}</b> 순으로 <b>1건만</b> 적용되고, 같은 종류가 2건 이상이면{' '}
            <b>할인값이 큰 것 → 할인값도 같으면 최신 등록된 것</b>이 적용됩니다. 할인 후 금액은 <b>하한이 보장</b>되어 0원 미만으로 내려가지
            않습니다.
            <br />
            <b>기간 프로모션</b>은 우선순위 없이 <b>모두 중복 적용</b>되며, <b>하한을 보장하지 않습니다.</b>
          </Hint>

          <Row>
            <RowLabel>
              할인 방식 <Req>*</Req>
            </RowLabel>
            <Controller
              control={form.control}
              name="discountType"
              render={({ field }) => (
                <Radios>
                  <label>
                    <input
                      type="radio"
                      checked={field.value === 'RATE'}
                      onChange={() => {
                        field.onChange('RATE')
                        clearDiscountValues()
                      }}
                    />
                    정률(%)
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={field.value === 'FIXED'}
                      onChange={() => {
                        field.onChange('FIXED')
                        clearDiscountValues()
                      }}
                    />
                    정액(원)
                  </label>
                </Radios>
              )}
            />
          </Row>

          {!isMonthly && (
            <>
              <Row>
                <RowLabel>
                  {discountValueLabel} <Req>*</Req>
                </RowLabel>
                <Controller
                  control={form.control}
                  name="discountValue"
                  render={({ field }) => (
                    <Suffix>
                      <Ctl
                        {...field}
                        value={toDiscountDisplay(field.value, isRate)}
                        placeholder={`${discountValueLabel}을 입력해 주세요.`}
                        onChange={(e) => field.onChange(limitDiscountInput(e.target.value, isRate))}
                        onBlur={(e) => {
                          if (isRate && outOfRateRange(e.target.value)) {
                            MAlert.show(RATE_RANGE_MESSAGE)
                            field.onChange('')
                          }
                          field.onBlur()
                        }}
                      />
                      <span>{isRate ? '%' : '원'}</span>
                    </Suffix>
                  )}
                />
              </Row>
              <Hint>
                {isRate
                  ? '0 초과 100 이하, 소수점 둘째 자리까지 입력 가능. 100% 입력은 전액 면제를 뜻합니다.'
                  : '0 이상의 정수만 입력 가능. 단가 금액을 초과할 수 없으며, 할인 결과가 음수가 되지 않도록 max(원가 − 할인액, 0) 으로 처리됩니다.'}
              </Hint>
              <FieldError message={errors.discountValue?.message} />
            </>
          )}

          {/* 수정에서는 잠근다 (LOGISPRD-11448) — BE 가 양방향 전환을 거부한다
              (PeriodPromotionValidator.validate · SETTLEMENT_CYCLE_NOT_CHANGEABLE).
              화면에서 안 막으면 눌리기만 하고 저장에서 400 이 난다 */}
          <Row>
            <RowLabel>
              정산 방식 <Req>*</Req>
            </RowLabel>
            <Controller
              control={form.control}
              name="settlementCycle"
              render={({ field }) => (
                <Radios>
                  <label>
                    <input type="radio" checked={field.value === 'DAILY'} disabled={isEdit} onChange={() => field.onChange('DAILY')} />
                    일별
                  </label>
                  <label>
                    <input type="radio" checked={field.value === 'MONTHLY'} disabled={isEdit} onChange={() => field.onChange('MONTHLY')} />
                    기간
                  </label>
                </Radios>
              )}
            />
          </Row>
          <Hint>
            {isMonthly ? (
              <>
                {/* 이 문구는 SG 확정본이다 — 임의로 바꾸지 않는다 */}
                <b>기간</b>: 프로모션 기간이 끝난 시점에 기간 전체를 누적 집계해 <b>1건의 할인 라인</b>으로 적재됩니다. 정산월은{' '}
                <b>프로모션 종료일이 속한 월</b>입니다.
                <br />
                기간 프로모션은 우선순위를 따지지 않고 <b>유효한 프로모션을 모두 중복 적용</b>하며, 할인은 원가 기준으로 각각 계산해 <b>합산</b>
                됩니다. (정액을 먼저 차감한 뒤 정률 적용)
                <br />
                <b>하한을 보장하지 않습니다.</b> 중복 적용으로 할인 합계가 원가를 넘으면 정산 금액이 음수가 될 수 있습니다.
              </>
            ) : (
              <>
                <b>일별</b>: 일정산 배치 시점에 건별로 할인이 적용됩니다. 대상이 겹치면 <b>1건만</b> 적용되며 할인을 합산하지 않습니다.
                <br />
                겹칠 때는 <b>{rankOrder}</b> 순으로 적용되고, 같은 종류가 2건 이상이면{' '}
                <b>할인값이 큰 것 → 할인값도 같으면 최신 등록된 것</b> 1건만 적용됩니다.
                <br />
                <b>하한을 보장합니다.</b> 할인 후 금액은 <b>max(원가 − 할인액, 0)</b> 으로 처리되어 음수가 되지 않습니다.
              </>
            )}
          </Hint>

          {isMonthly && (
            <Section>
              <h3>누적 수량 구간별 할인</h3>
              <TierTable>
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>구간</th>
                    <th>시작 수량</th>
                    <th>종료 수량</th>
                    <th>{isRate ? '할인율(%)' : '할인금액(원)'}</th>
                    <th style={{ width: 64 }} />
                  </tr>
                </thead>
                <tbody>
                  {tierFieldArray.fields.map((tierField, index) => (
                    <tr key={tierField.id}>
                      <td>{index + 1}</td>
                      <td>
                        <Controller
                          control={form.control}
                          name={`tiers.${index}.fromQuantity`}
                          render={({ field }) => (
                            // 2구간부터는 이전 구간 종료 수량 + 1 로 자동으로 채워지고, 직접 고칠 수 없다.
                            // disabled 는 폼 등록이 풀려 자동 채움이 화면에 반영되지 않으므로 readOnly 로 막는다
                            <Ctl
                              {...field}
                              placeholder={index > 0 ? '이전 종료 + 1' : '시작'}
                              readOnly={index > 0}
                              style={index > 0 ? { background: '#f5f5f5', color: '#888' } : undefined}
                              onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                          )}
                        />
                      </td>
                      <td>
                        <Controller
                          control={form.control}
                          name={`tiers.${index}.toQuantity`}
                          render={({ field }) => (
                            // 마지막 구간의 상한은 항상 열려 있어야 한다 (BE PeriodPromotionValidator — 열린 구간은 정확히 하나).
                            // 그래서 마지막 줄은 잠가 두고, 구간을 더 추가하면 그 줄이 마지막이 아니게 되며 열린다
                            <Ctl
                              {...field}
                              placeholder={index === tierFieldArray.fields.length - 1 ? '∞' : '종료'}
                              readOnly={index === tierFieldArray.fields.length - 1}
                              style={index === tierFieldArray.fields.length - 1 ? { background: '#f5f5f5', color: '#888' } : undefined}
                              onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ''))}
                              onBlur={(e) => {
                                // 타이핑 중에는 막지 않는다 (300 뒤에 2500 을 치려면 2·25 를 지나가야 한다).
                                // 칸을 벗어날 때 한 번만 비교해 알린다
                                const from = Number(values.tiers[index]?.fromQuantity)
                                if (e.target.value && from > 0 && Number(e.target.value) <= from) {
                                  MAlert.show(`${index + 1}구간의 종료 수량(${e.target.value})은 시작 수량(${from})보다 커야 합니다.`)
                                  field.onChange('')
                                }
                                field.onBlur()
                              }}
                            />
                          )}
                        />
                      </td>
                      <td>
                        <Controller
                          control={form.control}
                          name={`tiers.${index}.discountValue`}
                          render={({ field }) => (
                            <Ctl
                              {...field}
                              value={toDiscountDisplay(field.value, isRate)}
                              placeholder={isRate ? '할인율' : '할인금액'}
                              onChange={(e) => field.onChange(limitDiscountInput(e.target.value, isRate))}
                              onBlur={(e) => {
                                if (isRate && outOfRateRange(e.target.value, true)) {
                                  MAlert.show(`${index + 1}구간 — ${TIER_RATE_RANGE_MESSAGE}`)
                                  field.onChange('')
                                }
                                field.onBlur()
                              }}
                            />
                          )}
                        />
                      </td>
                      <td>
                        <TierButton
                          type="button"
                          // 구간은 MIN_TIERS 개 이상이어야 한다 (스키마 superRefine 과 같은 하한). 마지막 한 줄은 지울 수 없다
                          disabled={tierFieldArray.fields.length <= MIN_TIERS}
                          onClick={() => {
                            tierFieldArray.remove(index)
                            // 마지막이 된 줄의 상한은 다시 비운다 (열린 구간은 마지막 하나여야 한다)
                            const lastIndex = tierFieldArray.fields.length - 2
                            if (lastIndex >= 0) form.setValue(`tiers.${lastIndex}.toQuantity`, '', { shouldValidate: true })
                          }}
                        >
                          삭제
                        </TierButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TierTable>
              <TierButton
                type="button"
                disabled={tierFieldArray.fields.length >= MAX_TIERS}
                onClick={() => tierFieldArray.append({ ...EMPTY_TIER })}
              >
                + 구간 추가
              </TierButton>
              <Hint style={{ marginTop: 8, marginBottom: 0 }}>
                구간은 <b>{MIN_TIERS}개 이상 {MAX_TIERS}개 이하</b>로 등록합니다. 누적 수량은 <b>수량(pcs)</b> 기준이며, <b>입고비_BOX 항목만 수량(box)</b> 기준입니다. (송장
                수 · 주문 수 기준이 아닙니다)
                <br />
                마지막 구간의 종료 수량은 <b>이상</b>으로 고정되며, 구간을 더 추가하면 그 칸이 열립니다. 다음 구간은 <b>이전 종료 수량 + 1</b> 로
                자동으로 이어집니다.
              </Hint>
              <FieldError message={errors.tiers?.message || firstTierRowError} />
            </Section>
          )}

          <Row className="top">
            <RowLabel>
              프로모션 기간 <Req>*</Req>
            </RowLabel>
            <VStack gap={6}>
              <Controller
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <DatePickerInput
                    {...field}
                    minDate={dayjs(startMinDate).toDate()}
                    fixedCalendar
                    placement="top"
                    onChange={(date) => field.onChange(dayjs(date).format('YYYY-MM-DD'))}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <DatePickerInput
                    {...field}
                    minDate={
                      (values.startDate ? dayjs.max(dayjs(values.startDate), dayjs(endMinDate)) : dayjs(endMinDate))?.toDate() ??
                      dayjs(endMinDate).toDate()
                    }
                    fixedCalendar
                    placement="top"
                    onChange={(date) => field.onChange(dayjs(date).format('YYYY-MM-DD'))}
                  />
                )}
              />
            </VStack>
          </Row>
          <Hint>
            {isMonthly
              ? `기간 프로모션의 시작일은 ${startMinDate}부터 선택할 수 있습니다(라인에 카테고리가 적재되기 시작한 날). 종료일은 월정산이 마감되지 않은 기간만 가능해 ${endMinDate} 이후입니다. 월정산 마감일은 26일이며 12월만 31일입니다. 시작일 · 종료일 모두 필수이며, 종료일은 시작일과 같거나 이후여야 합니다.`
              : isEdit
                ? `수정은 시작일을 내일(${startMinDate}) 이후로만 옮길 수 있습니다. 오늘 시작인 프로모션은 이미 정산에 물려 있어 서버가 거부합니다. 종료일은 시작일과 같거나 이후여야 합니다.`
                : `일별 프로모션의 시작일은 오늘(${startMinDate})부터 선택할 수 있습니다. 시작일 · 종료일 모두 필수이며, 종료일은 시작일과 같거나 이후여야 합니다.`}
          </Hint>
          <FieldError message={errors.startDate?.message ?? errors.endDate?.message} />

          <Row className="top">
            <RowLabel>비고</RowLabel>
            <Controller
              control={form.control}
              name="remark"
              render={({ field }) => <Ctl {...field} maxLength={PROMOTION_REMARK_MAX_LENGTH} placeholder="비고를 입력해 주세요." />}
            />
          </Row>
          <FieldError message={errors.remark?.message} />
        </Body>
      </Modal>
    </form>
  )
}

// 시안 v4 마크업·CSS 이식 (MFS_프로모션관리_화면.html)
const Body = styled.div`
  font-size: 13.5px;
  color: #333;

  /* 입력 칸 너비를 한 값으로 고정한다. 1fr 로 두면 ScopeBox 안쪽 행만 패딩(13px)·테두리만큼
     좁아져 바깥 행과 크기가 어긋난다 (실측 319px vs 291px). */
  --ctl-w: 291px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 150px var(--ctl-w);
  align-items: center;
  gap: 12px;
  margin-bottom: 13px;

  &.top {
    align-items: start;
  }

  &.off > label {
    color: #bbb;
  }
`

const RowLabel = styled.label`
  font-size: 13.5px;
`

const Req = styled.span`
  color: #f4511e;
`

const Ctl = styled.input`
  width: 100%;
  height: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  padding: 0 9px;
  background: #fff;
  font-size: 13px;
  color: #333;

  &:disabled {
    background: #f5f5f5;
    color: #bbb;
  }
`

const SkuTextarea = styled.textarea`
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  padding: 6px 9px;
  font-size: 13px;
  color: #333;
  resize: vertical;
`

const Hint = styled.div`
  grid-column: 2;
  font-size: 11.5px;
  line-height: 1.6;
  color: #888;
  margin-top: -7px;
  margin-bottom: 11px;

  b {
    font-weight: 700;
    color: #666;
  }
`

const Radios = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  height: 32px;

  label {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    cursor: pointer;
  }
`

const Suffix = styled.div`
  position: relative;

  input {
    padding-right: 34px;
  }

  span {
    position: absolute;
    right: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
  }
`

const Section = styled.div`
  border: 1px solid #eee;
  background: #fbfbfb;
  border-radius: 3px;
  padding: 13px;
  margin-bottom: 14px;

  h3 {
    margin: 0 0 10px;
    font-size: 13px;
    font-weight: 700;
    color: #444;
  }
`

// 라벨 칸을 패딩(13px)+테두리(1px) 만큼 줄여, 안쪽 행의 입력 칸이 바깥 행과 같은 x 에서 시작하게 한다.
const ScopeBox = styled(Section)`
  ${Row} {
    grid-template-columns: 136px var(--ctl-w);
  }
`

const ScopeCaption = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #555;
  margin-bottom: 11px;

  span {
    font-weight: 400;
    color: #888;
  }
`

const TierTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th {
    text-align: left;
    color: #666;
    font-weight: 600;
    padding: 5px 6px;
  }

  td {
    padding: 5px 6px;
  }

  input {
    height: 28px;
  }
`

const TierButton = styled.button`
  height: 28px;
  padding: 0 10px;
  white-space: nowrap;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  background: #fff;
  font-size: 12px;
  color: #555;
  cursor: pointer;

  &:disabled {
    background: #f5f5f5;
    color: #bbb;
    cursor: not-allowed;
  }
`

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <ErrorText>{message}</ErrorText>
}

const ErrorText = styled.div`
  grid-column: 2;
  font-size: 11.5px;
  color: #d32f2f;
  margin-top: -7px;
  margin-bottom: 11px;
`

export default PromotionFormModal



